/**
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { ActionMetadata, ResolvableAction } from '@genkit-ai/core';
import type { ActionType } from '@genkit-ai/core/registry';
import type { GenerateMiddleware } from './generate/middleware.js';
import type { ModelAction } from './model.js';

/**
 * The v2 plugin interface for Genkit. Plugins implement this interface to provide
 * models, tools, middleware, and other actions to the Genkit framework.
 */
export interface GenkitPluginV2 {
  version: 'v2';
  name: string;
  init?: () => ResolvableAction[] | Promise<ResolvableAction[]>;
  resolve?: (
    actionType: ActionType,
    name: string
  ) => ResolvableAction | undefined | Promise<ResolvableAction | undefined>;
  list?: () => ActionMetadata[] | Promise<ActionMetadata[]>;

  // A shortcut for resolving a model.
  model(name: string): Promise<ModelAction>;

  // Returns a list of generate middleware to be used in `generate({use: [...])`.
  middleware?: () => GenerateMiddleware<any, any>[];
}
